/*
  Where the gap belongs while a row is being dragged, and therefore where the
  row will land when it is let go.

  The rule is the reference snippet's: the gap goes above the first row whose
  MIDDLE is below the pointer. Reading the middle rather than "which row is
  under the cursor" is what makes it settle -- the gap changes when the pointer
  crosses a row's centre, not when it crosses the boundary between two, so it
  does not flicker as the pointer sits on an edge.

  Kept apart from the component because it is the whole of the behaviour: get
  this wrong and the row lands somewhere other than the space that was showing,
  which is worse than having no preview.
*/

/*
  `rows` is [{ id, top, height }] in display order, with the dragged row already
  left out -- its own position is not a place it can land.

  Positions are the ones measured off the page, not computed from the list,
  because the gap row sits among the rows and moves them: what the reader is
  aiming at is where things ARE.
*/
export const gapTargetFor = (rows, clientY, endSentinel) => {
  const list = Array.isArray(rows) ? rows : [];
  const next = list.find((row) => clientY <= row.top + row.height / 2);
  /*
    Past every middle means past the last row, which is the one position no row
    can name -- there is nothing below it to sit above.
  */
  return next ? next.id : endSentinel;
};

/*
  Which row a finger is over, for reordering by the grip on a phone.

  A separate rule from gapTargetFor above, because the two gestures mean
  different things. A mouse drag inserts BEFORE a target, so the answer is a
  boundary. A finger drags a row onto another row and takes its place, so the
  answer is a row -- and every position is reachable that way, including the
  last, which is why this needs no end sentinel.

  `rows` must be the positions the rows had when the drag STARTED, not where
  they are now. During a touch drag the other rows slide out of the way, so
  reading their live positions would mean the row that moved aside is no longer
  under the finger, the target flips back, it slides in again, and the list
  shivers between two answers without the finger moving at all.

  Positions are document-relative for the same reason -- the page scrolls itself
  when the finger nears an edge, and viewport coordinates would drift under it.
*/
export const rowUnder = (rows, documentY) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const first = list[0];
  if (documentY <= first.top) return first.id;
  const last = list[list.length - 1];
  if (documentY >= last.top + last.height) return last.id;
  const hit = list.find(
    (row) => documentY >= row.top && documentY < row.top + row.height
  );
  // Between two bands only where border-spacing separates them; nearest wins.
  return hit ? hit.id : last.id;
};
